'use client'

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePathname } from 'next/navigation'
import clsx from 'clsx'
import type { AppDispatch } from '@/store'
import { changeNonMemberModalState } from '@/store/modal/slice'
import { selectIsAuthenticated, selectCurrentUser } from '@/store/user/slice'
import { selectMemberInfo, updateFavoriteStoryIds } from '@/store/member/slice'
import { MembersService } from '@/lib/api/services/members'
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys'
import { BUTTON_MESSAGES } from '@/constants/messages'
import styles from './styles.module.scss'

interface MyBookshelfProps {
  articleId: number
  articlePage?: boolean
  used?: string
  preview?: boolean
}

export default function MyBookshelf({
  articleId,
  articlePage = false,
  used = '',
  preview = false,
}: MyBookshelfProps) {
  const dispatch = useDispatch<AppDispatch>()
  const pathname = usePathname()
  const [isToggling, setIsToggling] = useState(false)
  const isTogglingRef = useRef(false) // 重複クリック防止用

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const currentUser = useSelector(selectCurrentUser)
  const memberInfo = useSelector(selectMemberInfo)

  // storeの状態から保存状態を取得
  const favoriteStoryIds = useMemo(
    () => memberInfo?.favoriteStoryIds || [],
    [memberInfo?.favoriteStoryIds],
  )
  const storeBasedSaved = favoriteStoryIds.some((id) => Number(id) === Number(articleId))

  // UIの即時変更用
  // Hydration Mismatch防止のため、初期値はfalseに固定
  // サーバー/クライアントで同じ初期値を使用し、マウント後にstoreの値で更新
  const [localSaved, setLocalSaved] = useState(false)

  // マウント後にstoreの値で初期化（Hydration Mismatch防止）
  // storeの値が変更されたらlocalSavedも同期（初期化やAPI成功後）
  useEffect(() => {
    setLocalSaved(storeBasedSaved)
  }, [storeBasedSaved])

  // sessionStorageのmemberデータをメモ化
  const memberData = useMemo(() => {
    if (typeof window !== 'undefined') {
      const data = sessionStorage.getItem(SESSION_STORAGE_KEYS.MEMBER)
      if (data) {
        try {
          return JSON.parse(data)
        } catch (error) {
          if (process.env.NODE_ENV !== 'production')
            console.error('sessionStorage member data parse error:', error)
          return null
        }
      }
    }
    return null
  }, [])

  const getAuthenticationInfo = () => {
    // sessionStorage優先、なければstoreから取得
    if (memberData) {
      return {
        isAuthenticated: !!memberData.id,
        userId: memberData.id?.toString() || null,
      }
    }

    // sessionStorageが使えない場合やDataがない場合はstoreから取得
    return {
      isAuthenticated,
      userId: currentUser?.id?.toString() || null,
    }
  }

  const toggleBookshelf = (event: React.MouseEvent) => {
    event.stopPropagation()

    const isPreview = /^\/preview\//.test(pathname)
    if (isPreview) return

    const { isAuthenticated: userAuthenticated, userId } = getAuthenticationInfo()

    // 未loginの場合はモーダル表示
    if (!userAuthenticated) {
      dispatch(changeNonMemberModalState(true))
      return
    }

    // login済み且つstoreにmemberDataが保持されている場合に実行
    if (!userId) return

    // 重複クリック防止
    if (isToggling || isTogglingRef.current) return

    // click時点のlocalSavedの値
    const beforeSaved = localSaved
    setIsToggling(true)
    isTogglingRef.current = true

    // UIの即時変更
    setLocalSaved(!beforeSaved)
    toggleBookshelfAction(userId, !beforeSaved)
  }

  const toggleBookshelfAction = async (memberId: string, isAdding: boolean) => {
    try {
      // isAddingによってendpointの切替
      isAdding
        ? await MembersService.addFavoriteStory(memberId, articleId.toString())
        : await MembersService.removeFavoriteStory(memberId, articleId.toString())

      // storeのDashbordを更新
      dispatch(updateFavoriteStoryIds({ storyId: articleId, isAdding }))
    } catch (error) {
      // エラー時は表示状態を元に戻す
      setLocalSaved(!isAdding)
    } finally {
      // isToggling(切替中の操作無効状態)をリセット
      setIsToggling(false)
      isTogglingRef.current = false
    }
  }

  const tooltipMessage = localSaved
    ? BUTTON_MESSAGES.REMOVE_FROM_BOOKSHELF
    : BUTTON_MESSAGES.ADD_TO_BOOKSHELF

  return (
    <div
      className={clsx(styles.myBookshelfWrapper, {
        [styles.cardButton]: !articlePage,
        [styles[used]]: used && styles[used],
      })}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.tooltipContainer}>
        <span
          className={clsx(styles.bookshelfButton, {
            [styles.active]: localSaved,
            noEvents: preview,
          })}
          onClick={toggleBookshelf}
          style={{ opacity: isToggling ? 0.7 : 1, pointerEvents: isToggling ? 'none' : 'auto' }}
        >
          <i className={clsx('icon-font-icon_bookmark_flag', styles.bookshelfIcon)} />
          {(used === 'articleReactionTop' || used === 'footerReaction') && (
            <span className={styles.bookshelfText}>マイ本棚</span>
          )}
        </span>
        {!articlePage && <span className={styles.tooltipText}>{tooltipMessage}</span>}
      </div>
    </div>
  )
}
